import type { TFunction } from 'i18next';
import { useRef, useState } from 'react';
import { CheckCircle, ChevronLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ValidationMessages } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSButtonGroup } from '@repo/sds/SDSButtonGroup';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSText } from '@repo/sds/SDSText';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';
import { Form, type FormConfig, type FormRef } from '@repo/ui/fixtures/form/components/Form/Form';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';

import './ChangePasswordModal.scss';

const createChangePasswordSchema = (requireCurrentPassword: boolean, t: TFunction<'common'>) =>
  z
    .object({
      currentPassword: requireCurrentPassword
        ? z.string().min(1, t('security.changePassword.currentPasswordRequired'))
        : z.string().optional(),
      newPassword: z
        .string()
        .min(12, ValidationMessages.PASSWORD_TOO_SHORT)
        .regex(/[a-zA-Z]/, ValidationMessages.PASSWORD_MISSING_LETTER)
        .regex(/\d/, ValidationMessages.PASSWORD_MISSING_NUMBER)
        .regex(/[^a-zA-Z\d]/, ValidationMessages.PASSWORD_MISSING_SPECIAL_SYMBOL),
      confirmPassword: z.string().min(1, t('security.changePassword.confirmPasswordRequired')),
    })
    .refine(
      data => {
        if (!requireCurrentPassword) {
          return true;
        }
        return data.newPassword !== data.currentPassword;
      },
      {
        path: ['newPassword'],
        message: ValidationMessages.PASSWORD_SAME_AS_CURRENT,
      },
    )
    .refine(data => data.confirmPassword === data.newPassword, {
      path: ['confirmPassword'],
      message: ValidationMessages.PASSWORD_DO_NOT_MATCH,
    });

export type ChangePasswordFormValues = {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
};

export interface ChangePasswordPayload {
  currentPassword?: string;
  newPassword: string;
}

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ChangePasswordPayload) => Promise<void>;
  hasPassword: boolean;
  successStep: number;
}

const INITIAL_VALUES: ChangePasswordFormValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const createChangePasswordFormConfig = (
  requireCurrentPassword: boolean,
  t: TFunction<'common'>,
): FormConfig<ChangePasswordFormValues> => {
  const fields: FormConfig<ChangePasswordFormValues> = [];

  if (requireCurrentPassword) {
    fields.push({
      componentType: 'FormTextInput',
      componentProps: {
        name: 'currentPassword',
        label: t('security.changePassword.currentPasswordLabel'),
        placeholder: t('security.changePassword.currentPasswordPlaceholder'),
        type: 'password',
        isRequired: true,
        dataTestId: DataTestIds.COMMON.SECURITY.CURRENT_PWD_INPUT,
        errorMessageTestId: DataTestIds.COMMON.SECURITY.CURRENT_PWD_ERROR,
      },
    });
  }

  fields.push(
    {
      componentType: 'FormTextInput',
      componentProps: {
        name: 'newPassword',
        label: t('security.changePassword.newPasswordLabel'),
        placeholder: t('security.changePassword.newPasswordPlaceholder'),
        type: 'password',
        isRequired: true,
        dataTestId: DataTestIds.COMMON.SECURITY.NEW_PASSWORD_INPUT,
        errorMessageTestId: DataTestIds.COMMON.SECURITY.NEW_PASSWORD_ERROR,
      },
    },
    {
      componentType: 'FormTextInput',
      componentProps: {
        name: 'confirmPassword',
        label: t('security.changePassword.confirmPasswordLabel'),
        placeholder: t('security.changePassword.confirmPasswordPlaceholder'),
        type: 'password',
        isRequired: true,
        dataTestId: DataTestIds.COMMON.SECURITY.CONFIRM_PASSWORD_INPUT,
        errorMessageTestId: DataTestIds.COMMON.SECURITY.CONFIRM_PASSWORD_ERROR,
      },
    },
  );

  return fields;
};

export function ChangePasswordModal({ isOpen, onClose, onSubmit, hasPassword, successStep }: ChangePasswordModalProps) {
  return (
    <ChangePasswordModalContent
      key={isOpen ? 'open' : 'closed'}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      hasPassword={hasPassword}
      successStep={successStep}
    />
  );
}

function ChangePasswordModalContent({ isOpen, onClose, onSubmit, hasPassword, successStep }: ChangePasswordModalProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useDeviceType();
  const formRef = useRef<FormRef<ChangePasswordFormValues>>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const title = hasPassword ? t('security.changePassword.changeTitle') : t('security.changePassword.setTitle');

  const regenerateForm = () => {
    setFormInstanceKey(prev => prev + 1);
  };

  const handleFormSubmit = async (values: ChangePasswordFormValues) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    await onSubmit({
      currentPassword: values.currentPassword || undefined,
      newPassword: values.newPassword,
    })
      .then(regenerateForm)
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t('security.changePassword.genericError');
        setFormError(message);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setFormError(null);
    regenerateForm();
    onClose();
  };

  const handlePrimaryAction = () => {
    formRef.current?.submitForm();
  };

  const form = (
    <>
      <Form
        key={formInstanceKey}
        ref={formRef}
        formConfig={createChangePasswordFormConfig(hasPassword, t)}
        validationSchema={createChangePasswordSchema(hasPassword, t)}
        defaultValues={INITIAL_VALUES}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        fieldsGap={isMobile ? 'text-large' : 'content-default'}
      />
      {formError && (
        <SDSText type="body-small" color="alert" text={formError} dataTestId={DataTestIds.COMMON.SECURITY.FORM_ERROR} />
      )}
    </>
  );

  const actions = (
    <SDSButtonGroup className="ChangePasswordPage__actions" isFullWidth justifyContent="flex-end">
      {isSubmitting ? null : (
        <SDSButton
          dataTestId={DataTestIds.MODALS.CONFIRMATION.CANCEL_BTN}
          mode="outline"
          onClick={handleClose}
          size="large"
          text={t('security.changePassword.cancel')}
        />
      )}
      <SDSButton
        dataTestId={DataTestIds.MODALS.CONFIRMATION.SAVE_CHANGES_BTN}
        isSubmitting={isSubmitting}
        mode="primary"
        onClick={handlePrimaryAction}
        size="large"
        text={t('security.changePassword.saveChanges')}
      />
    </SDSButtonGroup>
  );

  return (
    <>
      {isMobile ? (
        isOpen ? (
          <section aria-label={title} className="ChangePasswordPage">
            <SDSButton
              ariaLabel={t('security.changePassword.backToSecurity')}
              className="ChangePasswordPage__back"
              dataTestId={DataTestIds.COMMON.BACK_BUTTON}
              leftIcon={<SDSIcon icon={<ChevronLeft />} />}
              mode="ghost"
              onClick={handleClose}
              size="large"
              text={t('security.changePassword.backToSecurity')}
            />
            <SettingsCard className="ChangePasswordPage__card" direction="column">
              <SDSFlexbox className="ChangePasswordPage__fields" direction="column" fullWidth gap="text-x-large">
                <SDSText text={title} type="h4" />
                {form}
              </SDSFlexbox>
              {actions}
            </SettingsCard>
          </section>
        ) : null
      ) : (
        <SDSDialog
          isOpen={isOpen}
          onClose={handleClose}
          title={title}
          primaryAction={{
            dataTestId: DataTestIds.MODALS.CONFIRMATION.SAVE_CHANGES_BTN,
            label: t('security.changePassword.saveChanges'),
            onClick: handlePrimaryAction,
            loading: isSubmitting,
          }}
          secondaryAction={
            isSubmitting
              ? undefined
              : {
                  dataTestId: DataTestIds.MODALS.CONFIRMATION.CANCEL_BTN,
                  label: t('security.changePassword.cancel'),
                  onClick: handleClose,
                }
          }
        >
          <SDSFlexbox direction="column" gap="content-default">
            {form}
          </SDSFlexbox>
        </SDSDialog>
      )}
      <SDSDialog
        isOpen={!!successStep}
        onClose={() => onClose()}
        size="small"
        dataTestId={DataTestIds.COMMON.SECURITY.SUCCESS_MODAL}
      >
        <SDSFlexbox direction="column" alignItems="center" gap="no-design-16">
          <SDSIcon icon={<CheckCircle />} color="primary" size="large" />
          <SDSText
            type="h4"
            align="center"
            text={
              successStep === 1
                ? t('security.changePassword.passwordCreated')
                : t('security.changePassword.passwordChanged')
            }
          />
        </SDSFlexbox>
      </SDSDialog>
    </>
  );
}
